import React, { useEffect, useState, type ReactNode } from 'react';
import type { SlopeKeycloak } from '../core/index.js';
import { KeycloakContext, type KeycloakContextValue } from './KeycloakContext.js';

export interface KeycloakProviderProps {
  keycloak: SlopeKeycloak;
  children: ReactNode;
  onAuthSuccess?: () => void;
  onAuthError?: (error: unknown) => void;
  onAuthLogout?: () => void;
  onTokenExpired?: () => Promise<void>;
  enableToastAuth?: boolean;
}

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({
  keycloak,
  children,
  onAuthSuccess,
  onAuthError,
  onAuthLogout,
  onTokenExpired,
  enableToastAuth = true,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initializeKeycloak = async () => {
      try {
        // Check if Keycloak is already initialized
        if (keycloak.instance.didInitialize) {
          console.log('KeycloakProvider: Keycloak already initialized, skipping init');
          setIsAuthenticated(keycloak.instance.authenticated || false);
          setIsInitialized(true);
        } else {
          console.log('KeycloakProvider: Initializing Keycloak');
          await keycloak.init();
          setIsAuthenticated(keycloak.instance.authenticated || false);
          setIsInitialized(true);
        }

        // Setup event handlers
        keycloak.setupEventHandlers({
          onAuthSuccess: () => {
            setIsAuthenticated(true);
            onAuthSuccess?.();
          },
          onAuthError: (error) => {
            setIsAuthenticated(false);
            onAuthError?.(error);
          },
          onAuthLogout: () => {
            setIsAuthenticated(false);
            onAuthLogout?.();
          },
          onTokenExpired: async () => {
            if (onTokenExpired) {
              await onTokenExpired();
            } else {
              // Default token refresh behavior
              await keycloak.instance.updateToken(30);
            }
          },
        });
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
        setIsInitialized(true); // Mark as initialized even on error
      }
    };

    initializeKeycloak();
  }, [keycloak, onAuthSuccess, onAuthError, onAuthLogout, onTokenExpired]);

  const contextValue: KeycloakContextValue = {
    keycloak,
    isInitialized,
    isAuthenticated,
  };

  return (
    <KeycloakContext.Provider value={contextValue}>
      {isInitialized && children}
      {enableToastAuth && <KeycloakToastHandler keycloak={keycloak} />}
    </KeycloakContext.Provider>
  );
};

// Internal component to handle toast notifications for auth errors
const KeycloakToastHandler: React.FC<{ keycloak: SlopeKeycloak }> = ({ keycloak }) => {
  useEffect(() => {
    // This is handled by the Apollo Client error handling in the main app
    // but we could add global toast handling here if needed
  }, [keycloak]);

  return null;
};
